/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { Scenario }          from "@/models/scenario/scenario";
import {
  WorkBlock,
  WorkType
}                             from "@/models/scenario/work.block";

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineBasicMaterial,
  LineSegments,
  Plane
} from 'three';


interface TimeSpan {
  start: number;
  end:   number;
}

/**
 *
 * @class WorkBlocksBuilder
 *
 * this builder renders the work block scopes
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class WorkBlocksBuilder {

  /** the (maybe timezone specific) scenario */
  private       scenario: Scenario|null  = null;

  /** timescale gets clipped, so we don't go beyond simulation time */
  private  clippingPlane: Plane|null     = null;

  /** are we in dark or light mode? */
  private      nightMode: boolean        = true;

  /** the theme/user preferences from the main viewer */
  private          prefs: any            = {}

  /** standard constructor */
  constructor(init?: Partial<WorkBlocksBuilder>) {

    Object.assign(this, init);
  }

  /** force the scenario */
  public setScenario(scenario: Scenario): WorkBlocksBuilder {

    this.scenario = scenario;

    return this;
  }

  /** fetch the scenario */
  public getScenario(): Scenario {

    return this.scenario;
  }


  /** force the clipping extent */
  public setClippingPlane(clip: Plane): WorkBlocksBuilder {

    this.clippingPlane = clip;

    return this;
  }

  /** fetch the clipping extent */
  public getClippingPlane(): Plane {

    return this.clippingPlane;
  }

  /** force night mode  */
  public setNightMode(mode: boolean): WorkBlocksBuilder {

    this.nightMode = mode;

    return this;
  }

  /** fetch night mode */
  public getNightMode(): boolean {

    return this.nightMode;
  }

  /** force the theme */
  public setPrefs(prefs: any): WorkBlocksBuilder {

    this.prefs = prefs;

    return this;
  }

  /** fetch the theme */
  public getPrefs(): any {

    return this.prefs;
  }

  /** build the set of line segments as a complete geometry/mesh */
  public build(): LineSegments|null {

    let b: WorkBlocksBuilder = this;

    if (!b.scenario) {

      Logger.error(`[WorkBlocksBuilder] build - no scenario. Aborting.`);
      return null;
    }

    /* set up the work block outlines */

    /*
     * to know the max number of positions in advance we need a square, 4 segments and (x,y,z) for each
     * endpoint (4 X 6), we have to that square on each active day, and for each work block. So we have to
     * pre-scan the work blocks to get the total number of active days.
     *
     */

    let nDays: number = 0;

    for (const block of b.scenario.workBlocks().values()) {

      nDays += block._days.length;
    }

    /* configure ... */

    let geometry: BufferGeometry = new BufferGeometry();
    let positions: Float32Array  = new Float32Array(nDays * 4 * 6);
    let colors: Float32Array     = new Float32Array(nDays * 4 * 6);
    let indices: number[]        = [];
    let vIndex: number           = 0;
    let cIndex: number           = 0;
    let pIndex: number           = 0;
    let colorConverter     = new Color('#000000');

    geometry.name                = 'workzones';

    /*
     * walk the work blocks to figure out the set of active zones, and then for each active zone we draw
     * a rectangle to outline it on the graph.
     *
     */

    const blocks: Map<number, WorkBlock> = b.scenario.workBlocks();

    /* walk the blocks */

    for (const block of blocks.values()) {

      /* generate a set of times for this west/east span */

      const timeSpans: TimeSpan[] = [];

      for (const day of block._days) {

        timeSpans.push({
          start: day * 86400 + block._start,
          end:   day * 86400 + block._start + block._duration
        });
      }

      /* walk the time spans */

      for (const span of timeSpans) {

        const x1: number = span.start / 3600.0;
        const x2: number = span.end / 3600.0;
        const y1: number = block._from._mileage;
        const y2: number = block._to._mileage;

        const lines: any = [];

        /* construct the outline ... */

        lines.push([x1, y1, x2, y1]);
        lines.push([x2, y1, x2, y2]);
        lines.push([x2, y2, x1, y2]);
        lines.push([x1, y2, x1, y1]);

        /* set its color */

        if (b.nightMode) {

          switch (block._type) {
            case WorkType.CONSTRUCTION:
              colorConverter.set(b.prefs.night.workColor.construction);
              break;
            case WorkType.MEGABLOCK:
              colorConverter.set(b.prefs.night.workColor.megablock);
          }

        } else {

          switch (block._type) {
            case WorkType.CONSTRUCTION:
              colorConverter.set(b.prefs.day.workColor.construction);
              break;
            case WorkType.MEGABLOCK:
              colorConverter.set(b.prefs.day.workColor.megablock);
          }
        }

        /* create the bits we need to add it to the geometry ... */

        for (const line of lines) {

          const [sx, sy, ex, ey] = line;

          /* push this segment */

          positions[vIndex] = sx;
          positions[vIndex + 1] = sy;
          positions[vIndex + 2] = 0;

          positions[vIndex + 3] = ex;
          positions[vIndex + 4] = ey;
          positions[vIndex + 5] = 0;

          indices.push(pIndex);
          indices.push(pIndex + 1);

          vIndex += 6;
          pIndex += 2;

          /* push the color for this segment */

          colors[cIndex] = colorConverter.r;
          colors[cIndex + 1] = colorConverter.g;
          colors[cIndex + 2] = colorConverter.b;

          colors[cIndex + 3] = colorConverter.r;
          colors[cIndex + 4] = colorConverter.g;
          colors[cIndex + 5] = colorConverter.b;

          cIndex += 6;

        } /* end of segment walk within a move */

      } /* end of walking time and space spans */

    } /* end of walking blocks */

    /* time to render... */

    let material: LineBasicMaterial = new LineBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      clippingPlanes: [b.clippingPlane]
    });

    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    geometry.setAttribute('color', new BufferAttribute(colors, 3));

    /* all done */

    return new LineSegments(geometry, material);
  }
}
